import {
  AsyncPipe,
  Location,
  NgStyle,
  SlicePipe,
  TitleCasePipe,
  UpperCasePipe,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  HorizontalScrollerComponent,
  ButtonComponent,
  CircleIconCountryComponent,
  CircularButtonIconComponent,
  CommentCardComponent,
  EmptyCommentCard,
  HeaderBlockComponent,
  Information,
  RankingBlockComponent,
  ScenicCardComponent,
  SkeletonComponent,
  StravaSectionsBlockComponent,
  Top3,
  User,
  VideoSection,
  WatchActionsBlock,
  WatchDescriptionBlockComponent,
  Workout,
  Coaching,
  GpsOnly,
  Thumbnails,
} from '@kinomap-dev/ng-ui-lib';
import { Comment } from '@kinomap-dev/ng-ui-lib/lib/models/comment.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ModalService } from 'app/services/modal.service';
import { BehaviorSubject, map, Observable, of, Subscription } from 'rxjs';
import { KinoSubscription } from 'app/modules/dashboard/models/kino-subscription.model';
import { Store } from '@ngrx/store';
import { selectProfileData } from 'app/store/me/me.selectors';
import { selectActiveSubscription } from 'app/modules/dashboard/store/subscriptions/subscriptions.selectors';
import {
  configureIntegrateModal,
  configureShareModal,
  configureLikesModal,
  configureCommentsModal,
  configureReportModal,
  configurePlaylistModal,
  configureRankingModal,
  configureStravaModal,
  configureRecommendedModal,
  configureSameContributorModal,
  configureConnectionModal,
} from 'app/helpers/modal-helpers';
import { ActivatedRoute, Router } from '@angular/router';
import { ADMIN_SITE, environment } from 'src/environments/environment.local';
import { HttpParams } from '@angular/common/http';
import { StravaSection } from '@kinomap-dev/ng-ui-lib/lib/models/strava-section.model';
import { FavoriteApi } from '../../api/favorite.api';
import { RecommendedService } from '../../services/recommended.service';
import { EditScenicComponent } from '../../pages/scenic/components/editScenic/edit-scenic.component';
import { ToasterService } from 'app/services/toaster.service';
import { SwarmLoginService } from 'app/services';
import { checkScenicPermissions } from '../../helpers/checkScenicPermission.helpers';

@Component({
  selector: 'app-generic-view',
  templateUrl: './generic-view.component.html',
  styleUrls: ['./generic-view.component.scss'],
  imports: [
    TranslateModule,
    HeaderBlockComponent,
    CommentCardComponent,
    EmptyCommentCard,
    TitleCasePipe,
    UpperCasePipe,
    ButtonComponent,
    SkeletonComponent,
    CircleIconCountryComponent,
    CircularButtonIconComponent,
    WatchActionsBlock,
    WatchDescriptionBlockComponent,
    NgStyle,
    AsyncPipe,
    ScenicCardComponent,
    RankingBlockComponent,
    StravaSectionsBlockComponent,
    SlicePipe,
    HorizontalScrollerComponent,
    EditScenicComponent,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericViewComponent {
  @Input() backgroundImage: string | null = null;
  @Input() information: Workout | VideoSection | Coaching | GpsOnly | null =
    null;
  @Input() comments$: BehaviorSubject<Comment[]> | null = new BehaviorSubject<
    Comment[]
  >([]);
  recommended$: Observable<Workout[] | VideoSection[] | Coaching[] | GpsOnly[]>;
  @Input() top3$: Observable<Top3>;
  @Input() sameContributor$: Observable<Workout[] | VideoSection[]>;
  @Input() stravaSections$: Observable<StravaSection[]>;
  @Input() showRanking: boolean = false;
  @Input() isEditable: boolean = false;
  @Input() editLoading$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  @Output() onDownloadClick: EventEmitter<number> = new EventEmitter<number>();
  @Output() onLikeClick: EventEmitter<Information> =
    new EventEmitter<Information>();
  @Output() onFavoriteClick: EventEmitter<number> = new EventEmitter<number>();
  @Output() getComments: EventEmitter<void> = new EventEmitter<void>();
  @Output() getTop3: EventEmitter<void> = new EventEmitter<void>();
  @Output() onEditPost: EventEmitter<FormData> = new EventEmitter<FormData>();

  client: User | null = null;
  activeSubscription: KinoSubscription | null = null;
  modalRenderedSubscription: Subscription | null = null;
  subscriptions: Subscription[] = [];
  checkPermission: typeof checkScenicPermissions = checkScenicPermissions;

  // Flags pour éviter les appels multiples
  private recommendedLoaded: boolean = false;
  private previousInformationId: number | null = null;

  public location: Location = inject(Location);
  private toasterService: ToasterService = inject(ToasterService);
  private translate: TranslateService = inject(TranslateService);
  private favoriteService: FavoriteApi = inject(FavoriteApi);
  private modal: ModalService = inject(ModalService);
  private store: Store = inject(Store);
  private router: Router = inject(Router);
  private recommendedService: RecommendedService = inject(RecommendedService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private loginService: SwarmLoginService = inject(SwarmLoginService);

  private isAuthorized(
    actionType: string,
    options: { silent?: boolean } = {}
  ): boolean {
    const requiresAuthentication: boolean =
      actionType === 'likes' ||
      actionType === 'report' ||
      actionType === 'comments' ||
      actionType === 'profile' ||
      actionType === 'downloadGPX' ||
      actionType === 'playlist' ||
      actionType === 'ranking';

    if (requiresAuthentication && !this.client) {
      if (!options.silent) {
        this.openModal('connection');
      }
      return false;
    }

    // ? En attente de règle de restriction
    // if (!this.client || !this.activeSubscription) {
    //   this.toasterService.showWarning(
    //     this.translate.instant(
    //       'toaster.errors.notSubscribed.messages.toAccessFeature'
    //     )
    //   );
    //   return false;
    // }

    return true;
  }

  get authorProfileLink(): string | null {
    if (!this.information) {
      return null;
    }
    return this.isAuthorized('profile', { silent: true })
      ? `/dashboard/profile/${this.information.author.id}`
      : null;
  }

  getRecommended(): void {
    // Éviter les appels multiples si les données sont déjà chargées ou en cours de chargement
    if (
      !this.information ||
      this.recommendedLoaded ||
      this.previousInformationId === this.information.id
    ) {
      return;
    }

    this.recommendedLoaded = true;
    this.previousInformationId = this.information.id;
    this.recommended$ = this.recommendedService.getRecommended(
      this.information.type,
      this.information.sport
    );
  }

  toggleEditMode(): void {
    const queryParams: { [key: string]: any } = {
      ...this.route.snapshot.queryParams,
    };
    const queryParamsHandling: 'merge' | '' = !queryParams['edit']
      ? 'merge'
      : '';
    this.isEditable = !queryParams['edit'];
    if (!queryParams['edit']) {
      queryParams['edit'] = true;
    } else {
      delete queryParams['edit'];
    }
    this.router.navigate([], { queryParams, queryParamsHandling });
  }

  toggleEditAdminMode(): void {
    let editUrl: string = `${ADMIN_SITE}videos/edit/`;

    if (this.information.type === 'coaching') {
      editUrl += 'coaching/';
    }

    window.location.href = editUrl + this.information.id;
  }

  editPost(formValue: any) {
    this.onEditPost.emit(formValue);
  }

  toObservable(
    information: Information | StravaSection[]
  ): Observable<Information | StravaSection[]> {
    return of(information);
  }

  handleDownloadGPX(): void {
    if (!this.isAuthorized('downloadGPX')) return;
    this.onDownloadClick.emit(this.information.id);
  }

  handleFavoriteClick(isFavorite: boolean): void {
    const favoriteAction: Observable<void> = isFavorite
      ? this.favoriteService.deleteFavorite$(
          this.information.id,
          this.information.type
        )
      : this.favoriteService.postFavorite$(
          this.information.id,
          this.information.type
        );
    favoriteAction.subscribe();
  }

  handleLikeClick(): void {
    if (!this.isAuthorized('likes')) return;
    this.onLikeClick.emit(this.information);
    this.information.has_liked
      ? (this.information.likes_count -= 1)
      : (this.information.likes_count += 1);
    this.information.has_liked = !this.information.has_liked;
  }

  openModal(
    modalType:
      | 'integrate'
      | 'share'
      | 'likes'
      | 'comments'
      | 'report'
      | 'playlist'
      | 'ranking'
      | 'strava'
      | 'recommended'
      | 'sameContributor'
      | 'connection',
    data: any = {}
  ): void {
    if (!this.isAuthorized(modalType)) return;

    this.modalRenderedSubscription?.unsubscribe();
    const modalClassName: string = modalType + '-modal';
    this.modal.display(modalClassName);
    const modalConfig = {
      recommended: () => configureRecommendedModal(this.modal, data),
      sameContributor: () => configureSameContributorModal(this.modal, data),
      integrate: () => configureIntegrateModal(this.modal, this.information),
      share: () => configureShareModal(this.modal),
      likes: () =>
        configureLikesModal(
          this.modal,
          this.client,
          this.information,
          modalClassName,
          this.onLikeClick
        ),
      comments: () =>
        configureCommentsModal(
          this.modal,
          this.client,
          this.information,
          modalClassName,
          data,
          this.comments$
        ),
      report: () => configureReportModal(this.modal, this.information),
      playlist: () =>
        configurePlaylistModal(
          this.modal,
          this.information,
          this.toasterService,
          this.translate
        ),
      ranking: () =>
        configureRankingModal(
          this.modal,
          data,
          this.information,
          this.client,
          modalClassName
        ),
      strava: () => configureStravaModal(this.modal, data),
      connection: () => configureConnectionModal(this.modal, this.loginService),
    };

    this.modalRenderedSubscription = modalConfig[modalType]?.();
  }

  navigate(target: 'login' | 'watch' | 'author', id?: number): void {
    switch (target) {
      case 'login': {
        let params: HttpParams = new HttpParams()
          .set('client_id', '50')
          .set('redirect_uri', window.location.href)
          .set('state', Math.random().toString(36).substring(2, 15));

        if (window.location.href.includes('beta.kinomap.com')) {
          params = params.set('client_id', '54'); // le client ID du site-web sur beta.kinomap.com
        }

        const oauthUrl: string = `${environment.oauth.url}?${params.toString()}`;
        window.location.href = oauthUrl;
        return;
      }
      case 'watch': {
        this.router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            const type: string =
              this.information.type === 'scenic'
                ? 'watch'
                : this.information.type;
            this.router.navigate([`/browse/${type}`, id]);
          });
        return;
      }
      case 'author': {
        if (!this.isAuthorized('profile', { silent: true })) {
          this.openModal('connection');
          return;
        }
        this.router
          .navigate(['/dashboard/profile', id], {
            onSameUrlNavigation: 'reload',
            skipLocationChange: true,
          })
          .then((value: boolean) => value ?? window.location.reload());
        return;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Éviter les appels multiples - ne charger les recommandations que si information a changé et client existe
    if (
      changes['information'] &&
      changes['information'].currentValue &&
      this.client &&
      !changes['information'].firstChange
    ) {
      this.getRecommended();
    }
  }

  ngOnInit(): void {
    this.isEditable =
      this.route.snapshot.queryParams['edit'] === 'true' || false;
    this.subscriptions = [
      ...this.subscriptions,
      this.store.select(selectProfileData).subscribe((client: User) => {
        if (client) {
          this.client = client;
          // Ne lancer getComments et getTop3 qu'une seule fois quand le client est chargé
          if (!this.comments$.value.length) {
            this.getComments.emit();
          }
          this.getTop3.emit();
          // Charger les recommandations si les informations sont disponibles
          if (this.information && !this.recommended$) {
            this.getRecommended();
          }
        }
      }),
      this.store
        .select(selectActiveSubscription)
        .subscribe(
          (subscription: KinoSubscription) =>
            (this.activeSubscription = subscription)
        ),
    ];
  }

  getThumbnailUrl(thumbnails: string | Thumbnails): string {
    if (typeof thumbnails === 'string') {
      return thumbnails;
    }
    return thumbnails.large || thumbnails.medium || thumbnails.small || '';
  }

  ngOnDestroy(): void {
    this.modalRenderedSubscription?.unsubscribe();
    this.modal.reset();
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
